import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { track } from '@amplitude/analytics-browser'
import { AppPage, UnderlineTabs } from 'src/components/app-ui'
import { courseGetAction } from 'src/reducers/course/action'
import { getCollection } from 'src/reducers/collection/action'
import { COURSE_TABS, coursePath } from './tabs'
import SectionsTab from './sections-tab'
import LandingTab from './landing-tab'
import SettingsTab from './settings-tab'
import ThemeTab from './theme-tab'
import AnalyticsTab from './analytics-tab'
import ShareTab from './share-tab'
import UnpublishedBanner from './unpublished-banner'

const TAB_COMPONENTS = {
  sections: SectionsTab,
  'landing-page': LandingTab,
  settings: SettingsTab,
  theme: ThemeTab,
  analytics: AnalyticsTab,
  share: ShareTab,
}

const countPages = sections =>
  sections.reduce((total, section) => total + section.cards.length, 0)

/** Course overview with its tabs; editing happens in the editor. */
const CourseDetail = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { id, page } = useParams()
  const course = useSelector(state => state.course.course)
  const sections = useSelector(state => state.course.courseCards) || []
  const path = useSelector(state => state.collection.collection)

  const isLoaded = course && String(course.id) === String(id)

  useEffect(() => {
    dispatch(courseGetAction(id))
  }, [dispatch, id])

  // The back link names the course's learning path when it has one.
  const pathId = isLoaded ? course.courseCollectionId : null
  useEffect(() => {
    if (pathId && String(path?.id) !== String(pathId)) {
      dispatch(getCollection(pathId))
    }
  }, [dispatch, pathId, path])

  const activeTab = TAB_COMPONENTS[page] ? page : 'sections'
  const ActiveTab = TAB_COMPONENTS[activeTab]

  const openTab = value => {
    const tab = COURSE_TABS.find(t => t.value === value)
    track('course_page_selector', { pageView: tab.label })
    history.push(coursePath(id, value, tab.subPage))
  }

  const back = pathId
    ? {
        label: path?.name || 'Learning path',
        onClick: () => history.push(`/collection/${pathId}/mini-courses`),
      }
    : { label: 'Courses', onClick: () => history.push('/courses') }

  const pageCount = countPages(sections)
  const subtitle = isLoaded
    ? `${sections.length} ${
        sections.length === 1 ? 'section' : 'sections'
      } · ${pageCount} ${pageCount === 1 ? 'page' : 'pages'}`
    : ''

  return (
    <AppPage
      back={back}
      title={isLoaded ? course.name || 'Untitled course' : ''}
      subtitle={subtitle}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {isLoaded && <UnpublishedBanner course={course} />}
        <UnderlineTabs
          tabs={COURSE_TABS}
          value={activeTab}
          onChange={openTab}
        />
        {isLoaded && <ActiveTab course={course} sections={sections} />}
      </div>
    </AppPage>
  )
}

export default CourseDetail
